import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const parsePositivePage = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.floor(parsed);
};

const clampPage = (page, totalPages) => {
  const maxPage = Math.max(1, Number(totalPages) || 1);
  return Math.min(Math.max(1, parsePositivePage(page)), maxPage);
};

const buildSearchWithPage = (search, queryKey, page) => {
  const params = new URLSearchParams(search || '');
  if (page <= 1) {
    params.delete(queryKey);
  } else {
    params.set(queryKey, String(page));
  }
  const nextSearch = params.toString();
  return nextSearch ? `?${nextSearch}` : '';
};

export default function usePaginationHistory({
  currentPage,
  setCurrentPage,
  totalPages,
  queryKey = 'p',
  enabled = true,
  clampToTotalPages = false,
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPageRef = useRef(currentPage);
  const applyingLocationRef = useRef(false);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  const pageFromUrl = useMemo(() => {
    if (!enabled) return 1;
    const params = new URLSearchParams(location.search || '');
    return parsePositivePage(params.get(queryKey));
  }, [enabled, location.search, queryKey]);

  const replaceUrlPage = useCallback(
    (page) => {
      if (!enabled) return;
      const nextPage = clampToTotalPages ? clampPage(page, totalPages) : parsePositivePage(page);
      const nextSearch = buildSearchWithPage(location.search, queryKey, nextPage);
      if (nextSearch === (location.search || '')) return;
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    },
    [clampToTotalPages, enabled, location.pathname, location.search, navigate, queryKey, totalPages]
  );

  useEffect(() => {
    if (!enabled) return;
    if (pageFromUrl === currentPageRef.current) return;
    applyingLocationRef.current = true;
    setCurrentPage(pageFromUrl);
  }, [enabled, pageFromUrl, setCurrentPage]);

  useEffect(() => {
    if (!enabled) return;
    const nextPage = clampToTotalPages ? clampPage(currentPage, totalPages) : parsePositivePage(currentPage);
    if (clampToTotalPages && nextPage !== currentPage) {
      setCurrentPage(nextPage);
      return;
    }
    if (applyingLocationRef.current) {
      applyingLocationRef.current = false;
      return;
    }
    replaceUrlPage(nextPage);
  }, [clampToTotalPages, currentPage, enabled, replaceUrlPage, setCurrentPage, totalPages]);

  const goToPage = useCallback(
    (page) => {
      const nextPage = clampPage(page, totalPages);
      if (!enabled) {
        setCurrentPage(nextPage);
        return;
      }
      const nextSearch = buildSearchWithPage(location.search, queryKey, nextPage);
      if (nextSearch === (location.search || '')) {
        if (nextPage !== currentPageRef.current) {
          setCurrentPage(nextPage);
        }
        return;
      }
      navigate({ pathname: location.pathname, search: nextSearch });
    },
    [enabled, location.pathname, location.search, navigate, queryKey, setCurrentPage, totalPages]
  );

  return { goToPage, replaceUrlPage };
}
